import { Winda } from "@brexhq/next/src/utils/Winda";

//TODO: we need a way to not have to calculate this for every event , doing it this way now as this is the last point where we can call this function to ensure we are not ignoring any flag options, but ideally we can refactor this to be stored globally in state somewhere and only updated when the user changes their cookie preferences
import { getSegmentDestinationsFromCookies } from "../utils/analytics";

import {
  MetricEvent,
  EventNames,
  ButtonClickedEvent,
  InternalLinkClickedEvent,
  OutboundLinkClickedEvent,
  MarketingFieldFocusedEvent,
  AnnouncementBannerDismissedEvent,
  AnnouncementBannerShownEvent,
  ExperimentViewedEvent,
  FieldDirtiedEvent,
  FooterViewedEvent,
  FormSubmittedEvent,
  FilterSelected,
  MarketingFieldBlurredEvent,
  MarketingFormSubmittedEvent,
  NullEvent,
  PageNotFoundEvent,
  PageViewedEvent,
  SignupFieldBlurredEvent,
  SignupFieldDirtiedEvent,
  SignupFieldFocusedEvent,
  SignupUserInfoSubmittedEvent,
  SignupStepSubmittedEvent,
  SignupStepViewedEvent,
  CarouselCardChanged,
  IndustrySearchSent,
  IndustrySearchCompleted,
  IndustrySearchSelected,
  IndustryFormSubmitted,
  UserSoftSignupEvent,
  UserEarlyAccSoftSignupEvent,
  ArticleFeedbackEvent,
  HelpCenterArticleViewedEvent,
  HelpCenterSearchResultClickedEvent,
  HelpCenterSearchResultViewedEvent,
  HelpshiftShownEvent,
  MaintenanceScreenViewedEvent,
  InvestmentSourcesMultiEvent,
  FormStepViewedEvent,
  LeadRoutingEvent,
  OnboardingExperimentViewedEvent,
  ScrollingSubNavRowViewed,
  FilterCleared,
  ProductLogPinnedPostClicked,
  ProductLogSearchQuery,
  ProductLogPageNavigation,
  ErrorComponentShown,
  LeadFormViewedEvent,
  LeadFormSubmittedEvent,
  LeadUpdatedEvent,
  LeadCreatedEvent,
  LeadFormFocusedEvent,
  LeadFormBlurredEvent,
  ExperimentModuleInViewEvent,
  NoResultsViewShown,
  ClickFilterFromResourcesPage,
  ShowMoreArticlesClicked,
  OpenArticleFromSearchResults,
  ToggledContentHubFilters,
  ExternalEventPageViewed,
  PageLoadingSkeletonShown,
  ArticleNegativeFeedbackFormEvent,
  SupportChatTriggeredEvent,
  SupportChatOpenedEvent,
  SupportChatClosedEvent,
  OneClickSignInOptOutEvent,
  OneClickSignInOptInEvent,
  OneClickSignInRedirectEvent,
  OneClickSignInDialogDisplayedEvent,
} from "./analyticsEvents";

/**
 * Used to set the type as at least one of a predefined type property.
 * e.g. if type T { a: boolean; b: boolean }
 * AtLeastOnePropertyOf<T> = must have either a or b passed down.
 * Useful if we set a certain type to include everything, but for some functions need only one, like
 * our identify type call.
 *
 * Uncomment below to see an example of this type with the K defined
 *
  // type Test = {
  //   [K in keyof IntegrationsFlags]: {
  //     [L in K]: IntegrationsFlags[L] & {
  //       [L in Exclude<keyof IntegrationsFlags, K>]?: IntegrationsFlags[L];
  //     };
  //   };
  // }[keyof IntegrationsFlags];
 */
type AtLeastOnePropertyOf<T> = {
  [K in keyof T]: { [L in K]: T[L] } & { [L in Exclude<keyof T, K>]?: T[L] };
}[keyof T];

export type IdentifyEvent = {
  id?: string;
  properties: { [key: string]: any };
  /** e.g. { "Marketo V2": false } */
  integrations?: AtLeastOnePropertyOf<IntegrationsFlags>;
};

export type ApplicationLaunchedEvent = {
  type: EventNames.APPLICATION_LAUNCHED;
  time: number;
};

export type ApplicationRenderedEvent = {
  type: EventNames.APPLICATION_RENDERED;
  time: number;
};

export type TimingEvent = ApplicationLaunchedEvent | ApplicationRenderedEvent;

export interface AnalyticsApi {
  sendTiming(event: TimingEvent): void;
  trackEvent(event: AnalyticsEvent): void;
  identify(event: IdentifyEvent): void;
}

export class InitializedSegmentClient {
  // @ts-expect-error - Ignoring because tag is just there as a brand
  private tag = "InitializedSegmentClient";
  constructor(private window: Window) {}

  track(type: string, properties: Record<string, unknown>) {
    if (!this.window.analytics) return;
    if (Winda.isDataDogBot) return;
    const integrations = getSegmentDestinationsFromCookies();

    // We used to append event properties in Segment, thats no longer possible
    // so we do it here for every track event before it is sent to segment

    const path = window.location.pathname;
    const timestamp = new Date().toISOString();
    this.window.analytics.track(
      type,
      { ...properties, path, timestamp },
      { integrations },
    );
  }

  page(
    path: string,
    referrer: string,
    search: string,
    title: string,
    url: string,
  ) {
    if (!this.window.analytics) return;
    if (Winda.isDataDogBot) return;
    const integrations = getSegmentDestinationsFromCookies();

    this.window.analytics.page(
      {
        path,
        referrer,
        search,
        title,
        url,
      },
      { integrations },
    );
  }

  /**
   * Added customIntegrations. For Marketo, we want it to be always false
   * except for when we explicitly state it. Made the implementation generic
   * in case we run into other integrations with similar behaviour
   */
  identify(
    properties: Record<string, unknown>,
    id?: string,
    customIntegrations?: AtLeastOnePropertyOf<IntegrationsFlags>,
  ) {
    if (!this.window.analytics) return;
    if (Winda.isDataDogBot) return;
    const integrations = getSegmentDestinationsFromCookies();

    if (id)
      this.window.analytics.identify(id, properties, {
        integrations: { ...integrations, ...customIntegrations },
      });
    else
      this.window.analytics.identify(properties, {
        integrations: { ...integrations, ...customIntegrations },
      });
  }
}

export type IntegrationsFlags = {
  "Amazon S3": boolean;
  "Google Tag Manager": boolean;
  "Amplitude Actions": boolean;
  Mutiny: boolean;
  Amplitude: boolean;
  Hotjar: boolean;
  snowflake: boolean;
  "Marketo V2": boolean;
  Statsig: boolean;
};

export type AnalyticsEvent =
  | ButtonClickedEvent
  | InternalLinkClickedEvent
  | OutboundLinkClickedEvent
  | MarketingFieldFocusedEvent
  | MarketingFieldBlurredEvent
  | FormSubmittedEvent
  | MarketingFormSubmittedEvent
  | LeadFormFocusedEvent
  | LeadFormBlurredEvent
  | LeadFormViewedEvent
  | LeadFormSubmittedEvent
  | LeadCreatedEvent
  | LeadUpdatedEvent
  | SignupFieldFocusedEvent
  | SignupFieldBlurredEvent
  | SignupFieldDirtiedEvent
  | SignupStepSubmittedEvent
  | SignupStepViewedEvent
  | IndustrySearchSent
  | IndustrySearchCompleted
  | IndustrySearchSelected
  | IndustryFormSubmitted
  | FieldDirtiedEvent
  | SignupUserInfoSubmittedEvent
  | PageNotFoundEvent
  | ExperimentViewedEvent
  | FooterViewedEvent
  | MetricEvent
  | NullEvent
  | AnnouncementBannerDismissedEvent
  | AnnouncementBannerShownEvent
  | PageViewedEvent
  | CarouselCardChanged
  | UserSoftSignupEvent
  | UserEarlyAccSoftSignupEvent
  | ArticleFeedbackEvent
  | ArticleNegativeFeedbackFormEvent
  | SupportChatTriggeredEvent
  | SupportChatOpenedEvent
  | SupportChatClosedEvent
  | HelpCenterArticleViewedEvent
  | HelpshiftShownEvent
  | HelpCenterSearchResultClickedEvent
  | HelpCenterSearchResultViewedEvent
  | MaintenanceScreenViewedEvent
  | InvestmentSourcesMultiEvent
  | FilterSelected
  | FilterCleared
  | ProductLogPinnedPostClicked
  | FormStepViewedEvent
  | ExternalEventPageViewed
  | LeadRoutingEvent
  | OnboardingExperimentViewedEvent
  | ScrollingSubNavRowViewed
  | ProductLogSearchQuery
  | ProductLogPageNavigation
  | ErrorComponentShown
  | ExperimentModuleInViewEvent
  | NoResultsViewShown
  | ClickFilterFromResourcesPage
  | ShowMoreArticlesClicked
  | OpenArticleFromSearchResults
  | ToggledContentHubFilters
  | PageLoadingSkeletonShown
  | OneClickSignInOptInEvent
  | OneClickSignInOptOutEvent
  | OneClickSignInRedirectEvent
  | OneClickSignInDialogDisplayedEvent;

export type LogAnalyticsError = (error: Error) => void;
